import { Theme } from '@mui/material';
import { TOutreachType } from 'src/@types/outreachType';
import {
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  HomeIcon,
  MicrophoneIcon,
  PhoneIcon,
  Squares2X2Icon,
  TagIcon,
  BoltIcon,
  EnvelopeIcon,
  UsersIcon,
  UserIcon,
  FlagIcon,
  SignalSlashIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';

import {
  ChartBarIcon as SolidChartBarIcon,
  ChatBubbleLeftRightIcon as SolidChatBubbleLeftRightIcon,
  HomeIcon as SolidHomeIcon,
  MicrophoneIcon as SolidMicrophoneIcon,
  PhoneIcon as SolidPhoneIcon,
  Squares2X2Icon as SolidSquares2X2Icon,
  TagIcon as SolidTagIcon,
  BoltIcon as SolidBoltIcon,
  NewspaperIcon,
  EnvelopeIcon as SolidEnvelopeIcon,
  UsersIcon as SolidUsersIcon,
  UserIcon as SolidUserIcon,
  FlagIcon as SolidFlagIcon,
  SignalSlashIcon as SolidSignalSlashIcon,
  CurrencyDollarIcon as SolidCurrencyDollarIcon,
} from '@heroicons/react/24/solid';

type TOutreachTypeUserFlagged =
  | TOutreachType
  | 'user'
  | 'canvass-flagged'
  | 'canvass-no-gps'
  | 'paid';

// ----------------------------------------------------------------------

export function getColorByOutreachType(
  outreachType: TOutreachTypeUserFlagged | null,
  theme: Theme
) {
  switch (outreachType) {
    case 'call':
      return theme.palette.success.main;
    case 'canvass':
      return theme.palette.error.main;
    case 'canvass-flagged':
      return theme.palette.error.main;
    case 'canvass-no-gps':
      return theme.palette.error.main;
    case 'text':
      return theme.palette.info.main;
    case 'text-survey':
      return theme.palette.warning.main;
    case 'ivr':
      return theme.palette.primary.main;
    case 'tag':
      return theme.palette.secondary.main;
    case 'notes':
      return theme.palette.warning.main;
    case 'survey':
      return theme.palette.warning.main;
    case 'mail':
      return theme.palette.secondary.main;
    case 'relational':
      return theme.palette.info.main;
    case 'user':
      return theme.palette.secondary.main;
    case 'paid':
      return theme.palette.success.main;
    default:
      return theme.palette.warning.main;
  }
}

export function getIconByOutreachType(outreachType: TOutreachTypeUserFlagged | null, size = 18) {
  switch (outreachType) {
    case 'call':
      return <PhoneIcon height={size} />;
    case 'canvass':
      return <HomeIcon height={size} />;
    case 'canvass-flagged':
      return <FlagIcon height={size} />;
    case 'canvass-no-gps':
      return <SignalSlashIcon height={size} />;
    case 'text':
      return <ChatBubbleLeftRightIcon height={size} />;
    case 'text-survey':
      return <ChartBarIcon height={size} />;
    case 'ivr':
      return <MicrophoneIcon height={size} />;
    case 'tag':
      return <TagIcon height={size} />;
    case 'notes':
      return <NewspaperIcon height={size} />;
    case 'survey':
      return <BoltIcon height={size} />;
    case 'mail':
      return <EnvelopeIcon height={size} />;
    case 'relational':
      return <UsersIcon height={size} />;
    case 'user':
      return <UserIcon height={size} />;
    case 'paid':
      return <CurrencyDollarIcon height={size} />;
    default:
      return <Squares2X2Icon height={size} />;
  }
}

export function getSolidIconByOutreachType(
  outreachType: TOutreachTypeUserFlagged | null,
  size = 18
) {
  switch (outreachType) {
    case 'call':
      return <SolidPhoneIcon height={size} />;
    case 'canvass':
      return <SolidHomeIcon height={size} />;
    case 'canvass-flagged':
      return <SolidFlagIcon height={size} />;
    case 'canvass-no-gps':
      return <SolidSignalSlashIcon height={size} />;
    case 'text':
      return <SolidChatBubbleLeftRightIcon height={size} />;
    case 'text-survey':
      return <SolidChartBarIcon height={size} />;
    case 'ivr':
      return <SolidMicrophoneIcon height={size} />;
    case 'tag':
      return <SolidTagIcon height={size} />;
    case 'notes':
      return <NewspaperIcon height={size} />;
    case 'survey':
      return <SolidBoltIcon height={size} />;
    case 'mail':
      return <SolidEnvelopeIcon height={size} />;
    case 'relational':
      return <SolidUsersIcon height={size} />;
    case 'user':
      return <SolidUserIcon height={size} />;
    case 'paid':
      return <SolidCurrencyDollarIcon height={size} />;
    default:
      return <SolidSquares2X2Icon height={size} />;
  }
}

export function getLabelByOutreachType(outreachType: TOutreachTypeUserFlagged | null) {
  switch (outreachType) {
    case 'call':
      return 'Phone Banking';
    case 'canvass':
      return 'Canvassing';
    case 'canvass-flagged':
      return 'Canvassing Flagged';
    case 'canvass-no-gps':
      return 'Canvassing No GPS';
    case 'text':
      return 'Text';
    case 'text-survey':
      return 'Text Survey';
    case 'ivr':
      return 'Call Survey (IVR)';
    case 'tag':
      return 'Tag';
    case 'notes':
      return 'Notes';
    case 'survey':
      return 'Survey';
    case 'mail':
      return 'Mail';
    case 'relational':
      return 'Relational Text';
    case 'user':
      return 'User';
    case 'paid':
      return 'Payment / Verification';
    default:
      return 'Default';
  }
}
